import {Amplify} from 'aws-amplify';
import {
  InitWithAmplify,
  InitEnvironmentVariable,
  ProgramService as PikadoProgramService,
  ProgramRepository,
} from '@PikaDoApp/npm-pikado-logic';
import type {
  CreateNewProgramInput,
  UpdateNewProgramInput,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/program/graphql/program.interface';
import {IProgramsNewRepository} from './IProgramsNewRepository';
import {getEnvironment} from '../../lib/getEnvironment';

// Initialize the Pikado Logic package
InitWithAmplify(Amplify);
InitEnvironmentVariable(getEnvironment());

// ProgramService requires a ProgramRepository instance
const pikadoProgramRepository = new ProgramRepository();
const pikadoProgramService = new PikadoProgramService(pikadoProgramRepository);

export class ProgramsNewRepository implements IProgramsNewRepository {
  async getNewPrograms(): Promise<any[]> {
    try {
      // Using @PikaDoApp/npm-pikado-logic package
      const programsResponse = await pikadoProgramService.getPrograms();

      if (!programsResponse) {
        return [];
      }

      // Transform NewProgram[] to match UI expectations
      // Map _id to id for UI compatibility
      const transformedPrograms = programsResponse.map(program => ({
        id: program._id,
        name: program.name,
        createdBy: program.createdBy,
        isDeleted: program.isDeleted,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      }));

      return transformedPrograms;
    } catch (error) {
      console.error(
        '🔍 [PROGRAM-NEW-PACKAGE] getNewPrograms repository Error:',
        error,
      );
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  async getNewProgramById(programId: string): Promise<any> {
    try {
      // Using @PikaDoApp/npm-pikado-logic package
      const programResponse =
        await pikadoProgramService.getProgramById(programId);

      if (!programResponse) {
        return null;
      }

      const program = programResponse;

      if (!program) {
        return null;
      }

      // Transform NewProgram to match UI expectations
      // Map _id to id for UI compatibility
      const transformedProgram = {
        id: program._id,
        name: program.name,
        createdBy: program.createdBy,
        isDeleted: program.isDeleted,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return transformedProgram;
    } catch (error) {
      console.error(
        '🔍 [PROGRAM-NEW-PACKAGE] getNewProgramById repository Error:',
        error,
      );
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  async createNewProgram(program: CreateNewProgramInput): Promise<any> {
    try {
      // Using @PikaDoApp/npm-pikado-logic package
      const createdProgram =
        await pikadoProgramService.createNewProgram(program);

      if (!createdProgram) {
        return null;
      }

      // Transform created program to match UI expectations
      const transformedProgram = {
        id: createdProgram._id,
        name: createdProgram.name,
        createdBy: createdProgram.createdBy,
        isDeleted: createdProgram.isDeleted,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return transformedProgram;
    } catch (error) {
      console.error(
        '🔍 [PROGRAM-NEW-PACKAGE] createNewProgram repository Error:',
        error,
      );
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  async updateNewProgram(program: UpdateNewProgramInput): Promise<any> {
    try {
      // Using @PikaDoApp/npm-pikado-logic package
      const updatedProgram =
        await pikadoProgramService.updateNewProgram(program);

      if (!updatedProgram) {
        return null;
      }

      // Transform updated program to match UI expectations
      const transformedProgram = {
        id: updatedProgram._id,
        name: updatedProgram.name,
        createdBy: updatedProgram.createdBy,
        isDeleted: updatedProgram.isDeleted,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      return transformedProgram;
    } catch (error) {
      console.error(
        '🔍 [PROGRAM-NEW-PACKAGE] updateNewProgram repository Error:',
        error,
      );
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  async softDeleteNewProgram(programId: string): Promise<boolean> {
    try {
      // Using @PikaDoApp/npm-pikado-logic package
      const result = await pikadoProgramService.softDeleteProgram(programId);
      return result;
    } catch (error) {
      console.error(
        '🔍 [PROGRAM-NEW-PACKAGE] softDeleteNewProgram repository Error:',
        error,
      );
      throw error instanceof Error ? error : new Error(String(error));
    }
  }
}
